import {
  SECTION,
  serviceTypeHasCmsSubmission,
} from "constants/SettingsConstants";
import type {
  AmendedReviewScreeningStatus,
  LienStatus,
  LienValidation,
  MSAStatus,
  MSAValidation,
} from "types/DataTypes";

export type StatusColor = "Red" | "Yellow" | "Green";

export type StatusTitle = {
  status: StatusColor;
  url: string;
};

export type StatusBlockProps = {
  title: string;
  status: StatusColor;
  url: string;
  disabled?: boolean;
};

// PageType is what the URL alone can tell us. Every MSA flavor (MSA, NTMSA,
// MSA-111, Pre-MSA) lives under /future-medical/msa/, so getPageType can only
// ever say "MSA" for them; the concrete flavor comes from the loaded service
// and is carried in ValidationPayload.type instead, which is why that union is
// wider than this one.
export type PageType =
  | typeof SECTION.MSA
  | typeof SECTION.LIENS
  | typeof SECTION.AMENDED_REVIEW
  | typeof SECTION.AMENDED_REVIEW_SCREENING
  | typeof SECTION.LCP
  | typeof SECTION.MCP
  | typeof SECTION.PRE_MSA
  | null;

export const validateStatus = (status: unknown): StatusColor => {
  if (
    typeof status === "string" &&
    ["Red", "Yellow", "Green"].includes(status)
  ) {
    return status as StatusColor;
  }
  return "Red"; // fallback to red if invalid
};

export const getPageType = (url: string): PageType => {
  const pathSegments = url.split("/").filter(Boolean);

  // handle future-medical sub-paths
  if (pathSegments[0] === "future-medical") {
    const subType = pathSegments[1];
    switch (subType) {
      case "msa":
        return SECTION.MSA;
      case "msa-111":
        return SECTION.MSA;
      case "lcp":
        return SECTION.LCP;
      case "mcp":
        return SECTION.MCP;
      case "ar-screening":
        return SECTION.AMENDED_REVIEW_SCREENING;
      case "amended-review":
        return SECTION.AMENDED_REVIEW;
      default:
        return null;
    }
  }

  // handle other root paths, more later
  switch (pathSegments[0]) {
    case "liens":
      return SECTION.LIENS;
    default:
      return null;
  }
};

// union type for validation payload, so we can pass in either MSAValidation or LienValidation
export type ValidationPayload =
  | {
      type:
        | typeof SECTION.MSA
        | typeof SECTION.MSA_111
        | typeof SECTION.LCP
        | typeof SECTION.MCP
        | typeof SECTION.AMENDED_REVIEW
        | typeof SECTION.AMENDED_REVIEW_SCREENING
        | typeof SECTION.PRE_MSA
        | typeof SECTION.NTMSA;
      validation: MSAValidation;
      id: string;
    }
  | {
      type: typeof SECTION.LIENS;
      validation: LienValidation;
      id: string;
      lienFileId: string;
      fileId: string;
    };

export const getStatusTitles = (
  payload: ValidationPayload,
  quickbooksIntegrationEnabled: boolean = true,
): Record<string, StatusTitle> => {
  const { type, validation, id } = payload;

  // type guard for liens
  if (type === SECTION.LIENS && validation?.status) {
    const { lienFileId, fileId } = payload;
    const status = validation.status as LienStatus;
    const titles: Record<string, StatusTitle> = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/liens/${lienFileId}/lien/${id}/operations`,
      },
      Query: {
        status: validateStatus(status.query),
        url: `/liens/${lienFileId}/query`,
      },
      "Investigate DOI": {
        status: validateStatus(status["investigate-doi"]),
        url: `/liens/${lienFileId}/lien/${id}/investigate-doi`,
      },
      Analysis: {
        status: validateStatus(status.analysis),
        url: `/liens/${lienFileId}/lien/${id}/analysis`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/liens/${lienFileId}/invoice?file_id=${fileId}`,
        },
      }),
      "Client Submission": {
        status: validateStatus(status["client-submission"]),
        url: `/liens/${lienFileId}/lien/${id}/client-submission`,
      },
      Appeal: {
        status: validateStatus(status.appeal),
        url: `/liens/${lienFileId}/lien/${id}/appeal`,
      },
      "Case Closure": {
        status: validateStatus(status["case-closure"]),
        url: `/liens/${lienFileId}/lien/${id}/case-closure`,
      },
    };

    return titles;
  }

  // MSA
  if (
    (type === SECTION.MSA ||
      type === SECTION.MSA_111 ||
      type === SECTION.NTMSA ||
      type === SECTION.PRE_MSA) &&
    validation?.status
  ) {
    const status = validation.status as MSAStatus;
    const titles = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/future-medical/msa/${id}/operations`,
      },
      Query: {
        status: validateStatus(status.query),
        url: `/future-medical/msa/${id}/query`,
      },
      "Rated Age": {
        status: validateStatus(status["rated-age"]),
        url: `/future-medical/msa/${id}/rated-age`,
      },
      "Medical Review": {
        status: validateStatus(status["medical-review"]),
        url: `/future-medical/msa/${id}/medical-review`,
      },
      "Legal Review": {
        status: validateStatus(status["legal-review"]),
        url: `/future-medical/msa/${id}/legal-review`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/future-medical/msa/${id}/invoice`,
        },
      }),
      "Client Delivery": {
        status: validateStatus(status["client-delivery"]),
        url: `/future-medical/msa/${id}/client-delivery`,
      },
      // NTMSA / MSA-111 / Pre-MSA are never submitted to CMS, so they have no CMS segments
      ...(serviceTypeHasCmsSubmission(type) && {
        "CMS Submission": {
          status: validateStatus(status["cms-submission"]),
          url: `/future-medical/msa/${id}/cms-submission`,
        },
        "Invoice (CMS)": {
          status: validateStatus(status["invoice-cms"]),
          url: `/future-medical/msa/${id}/invoice-cms`,
        },
        "CMS Status": {
          status: validateStatus(status["cms-status"]),
          url: `/future-medical/msa/${id}/cms-status`,
        },
        "F.S.D": {
          status: validateStatus(status.fsd),
          url: `/future-medical/msa/${id}/fsd`,
        },
      }),
    };
    return titles;
  }

  // MCP
  if (type === SECTION.MCP && validation?.status) {
    const status = validation.status as MSAStatus;
    const titles = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/future-medical/mcp/${id}/operations`,
      },
      Query: {
        status: validateStatus(status.query),
        url: `/future-medical/mcp/${id}/query`,
      },
      "Rated Age": {
        status: validateStatus(status["rated-age"]),
        url: `/future-medical/mcp/${id}/rated-age`,
      },
      "Medical Review": {
        status: validateStatus(status["medical-review"]),
        url: `/future-medical/mcp/${id}/medical-review`,
      },
      "Legal Review": {
        status: validateStatus(status["legal-review"]),
        url: `/future-medical/mcp/${id}/legal-review`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/future-medical/mcp/${id}/invoice`,
        },
      }),
      "MCP to Client": {
        status: validateStatus(status["client-delivery"]),
        url: `/future-medical/mcp/${id}/mcp-to-client`,
      },
    };
    return titles;
  }

  // LCP
  if (type === SECTION.LCP && validation?.status) {
    const status = validation.status as MSAStatus;
    const titles = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/future-medical/lcp/${id}/operations`,
      },
      Query: {
        status: validateStatus(status.query),
        url: `/future-medical/lcp/${id}/query`,
      },
      "Rated Age": {
        status: validateStatus(status["rated-age"]),
        url: `/future-medical/lcp/${id}/rated-age`,
      },
      "Medical Review": {
        status: validateStatus(status["medical-review"]),
        url: `/future-medical/lcp/${id}/medical-review`,
      },
      "Legal Review": {
        status: validateStatus(status["legal-review"]),
        url: `/future-medical/lcp/${id}/legal-review`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/future-medical/lcp/${id}/invoice`,
        },
      }),
      "LCP to Client": {
        status: validateStatus(status["client-delivery"]),
        url: `/future-medical/lcp/${id}/lcp-to-client`,
      },
    };
    return titles;
  }

  // Amended Review
  if (type === SECTION.AMENDED_REVIEW && validation?.status) {
    const status = validation.status as MSAStatus;
    const titles = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/future-medical/amended-review/${id}/operations`,
      },
      Query: {
        status: validateStatus(status.query),
        url: `/future-medical/amended-review/${id}/query`,
      },
      "Rated Age": {
        status: validateStatus(status["rated-age"]),
        url: `/future-medical/amended-review/${id}/rated-age`,
      },
      "Medical Review": {
        status: validateStatus(status["medical-review"]),
        url: `/future-medical/amended-review/${id}/medical-review`,
      },
      "Legal Review": {
        status: validateStatus(status["legal-review"]),
        url: `/future-medical/amended-review/${id}/legal-review`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/future-medical/amended-review/${id}/invoice`,
        },
      }),
      "Client Delivery": {
        status: validateStatus(status["client-delivery"]),
        url: `/future-medical/amended-review/${id}/client-delivery`,
      },
      "CMS Submission": {
        status: validateStatus(status["cms-submission"]),
        url: `/future-medical/amended-review/${id}/cms-submission`,
      },
      "Invoice (CMS)": {
        status: validateStatus(status["invoice-cms"]),
        url: `/future-medical/amended-review/${id}/invoice-cms`,
      },
      "CMS Decision": {
        status: validateStatus(status["cms-status"]),
        url: `/future-medical/amended-review/${id}/cms-status`,
      },
      "F.S.D": {
        status: validateStatus(status.fsd),
        url: `/future-medical/amended-review/${id}/fsd`,
      },
    };
    return titles;
  }

  // Amended Review Screening
  if (type === SECTION.AMENDED_REVIEW_SCREENING && validation?.status) {
    const status = validation.status as AmendedReviewScreeningStatus;
    const titles = {
      Operations: {
        status: validateStatus(status.operations),
        url: `/future-medical/ar-screening/${id}/operations`,
      },
      "10/10 Review": {
        status: validateStatus(status.review),
        url: `/future-medical/ar-screening/${id}/amended-review`,
      },
      "Client Analysis": {
        status: validateStatus(status.client_analysis),
        url: `/future-medical/ar-screening/${id}/analysis`,
      },
      ...(quickbooksIntegrationEnabled && {
        Invoice: {
          status: validateStatus(status.invoice),
          url: `/future-medical/ar-screening/${id}/invoice`,
        },
      }),
    };
    return titles;
  }

  return {};
};
